import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { EXPLORATION_CARD_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';

/**
 * Topic exploration: one card at a time, large Next / Finish controls (tap-first).
 * Content: { cards: [{ id, title, body, followUpQuestion? }] }
 */
export default function ExplorationCardEngine({
  title = 'Explore',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_exploration_cards',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  onRegisterLessonFooterActions,
  lessonUiMode = 'default',
  guidedContext,
}) {
  const cards = content.cards || [];
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const suppressActivityTitle = shouldSuppressActivityTitle({ stepTitle, activityTitle: title });
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);
  const [showCelebration, celebrate] = useCelebration(1800);
  const [a11yMsg, setA11yMsg] = useState('');
  const tapLesson = isTapFirstLessonUi(lessonUiMode);

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: EXPLORATION_CARD_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: done,
    enabled: guidedHintsEnabled && cards.length > 0,
  });

  const { noteUserAction, noteCorrectAnswer } = guided;

  const card = cards[index];

  const finishRef = useRef(() => {});
  const goNextRef = useRef(() => {});
  const shellLesson = typeof onRegisterLessonFooterActions === 'function';

  const goNext = () => {
    if (index < cards.length - 1) {
      noteUserAction();
      const ni = index + 1;
      setIndex(ni);
      onProgress?.({ activityId, engine: 'exploration_card', cardIndex: ni, total: cards.length });
      trackMiniAppEvent.interaction(activityId, 'next_card');
      setA11yMsg(`Card ${ni + 1} of ${cards.length}`);
    }
  };

  const goPrev = () => {
    if (index > 0) {
      noteUserAction();
      const ni = index - 1;
      setIndex(ni);
      onProgress?.({ activityId, engine: 'exploration_card', cardIndex: ni, total: cards.length });
    }
  };

  const finish = () => {
    noteUserAction();
    noteCorrectAnswer();
    setDone(true);
    celebrate();
    setA11yMsg('You finished the tour!');
    onComplete?.({ activityId, engine: 'exploration_card', cardsViewed: cards.length });
    trackMiniAppEvent.completed(activityId);
  };

  finishRef.current = finish;
  goNextRef.current = goNext;

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (done) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    if (shellLesson) {
      const isLastCard = index >= cards.length - 1;
      onRegisterLessonFooterActions({
        continueLesson: () => {
          if (isLastCard) finishRef.current();
          else goNextRef.current();
        },
      });
      return () => onRegisterLessonFooterActions(null);
    }
    onRegisterLessonFooterActions({
      showAnswer: () => finishRef.current(),
    });
    return () => onRegisterLessonFooterActions(null);
  }, [onRegisterLessonFooterActions, done, shellLesson, index, cards.length]);

  if (cards.length === 0) {
    return (
      <div style={getEngineRootStyle({ singleSurface })}>
        <p style={instructionsStyle}>No cards in this activity yet.</p>
      </div>
    );
  }

  if (done) {
    return (
      <div style={getEngineRootStyle({ singleSurface })}>
        <CelebrationOverlay visible={showCelebration} message="Nice exploring!" />
        <A11yAnnouncement message={a11yMsg} />
        <h2 style={titleStyle}>You finished!</h2>
        <p style={instructionsStyle}>Come back anytime to read these cards again.</p>
      </div>
    );
  }

  const shell = {
    ...getEngineRootStyle({ singleSurface, maxWidth: 640 }),
    margin: '0 auto',
    padding: tapLesson ? '0 0 4px' : '0 0 8px',
    position: 'relative',
    boxSizing: 'border-box',
    ...(guided.contentReserveStyle || {}),
  };

  const primaryInCard = {
    ...primaryBtn,
    width: '100%',
    marginTop: 14,
    marginBottom: 0,
    minHeight: 56,
    padding: '14px 18px',
    fontSize: 16,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    touchAction: 'manipulation',
  };

  const cardSurface = {
    padding: tapLesson ? '14px 14px 16px' : '18px 16px',
    borderRadius: 16,
    background: singleSurface ? 'rgba(15, 23, 42, 0.03)' : 'var(--color-bg)',
    border: singleSurface ? 'none' : '1px solid var(--color-border)',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
  };

  const isLast = index >= cards.length - 1;

  return (
    <div style={shell}>
      <CelebrationOverlay visible={showCelebration} message="Nice exploring!" />
      <A11yAnnouncement message={a11yMsg} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!tapLesson && title && !suppressActivityTitle ? (
        <h2 style={{ ...titleStyle, marginBottom: 10 }}>{title}</h2>
      ) : null}

      <div role="article" aria-label={`Card ${index + 1} of ${cards.length}`} style={cardSurface}>
        <h3 style={{ ...titleStyle, fontSize: '1.15rem', margin: '0 0 10px', lineHeight: 1.25 }}>
          {card.title}
        </h3>
        <p
          style={{
            ...instructionsStyle,
            margin: '0 0 12px',
            fontSize: 15,
            lineHeight: 1.45,
          }}
        >
          {card.body}
        </p>
        {card.followUpQuestion ? (
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--color-text-soft, var(--color-text))',
              lineHeight: 1.4,
              opacity: 0.88,
            }}
          >
            Think about: {card.followUpQuestion}
          </p>
        ) : null}

        {!shellLesson ? (
          <button
            type="button"
            style={primaryInCard}
            onClick={isLast ? finish : goNext}
            aria-label={isLast ? 'Finish tour' : 'Next card'}
          >
            {isLast ? 'Finish tour' : 'Next card →'}
          </button>
        ) : null}

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 6,
            marginTop: 12,
            flexWrap: 'wrap',
          }}
          aria-hidden
        >
          {cards.map((_, i) => (
            <span
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: i === index ? 'var(--color-primary)' : 'var(--color-border)',
                opacity: i === index ? 1 : 0.55,
              }}
            />
          ))}
        </div>

        {!tapLesson ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              style={{ ...secondaryBtn, flex: 1, minHeight: 48 }}
              onClick={goPrev}
              disabled={index === 0}
            >
              Back
            </button>
          </div>
        ) : index > 0 ? (
          <button
            type="button"
            style={{
              ...secondaryBtn,
              marginTop: 10,
              width: '100%',
              minHeight: 44,
              fontSize: 14,
              opacity: 0.92,
            }}
            onClick={goPrev}
          >
            ← Previous card
          </button>
        ) : null}
      </div>
    </div>
  );
}
